import React from 'react';

import { Box, Center, HStack, Stack } from '@chakra-ui/react';
import toLower from 'lodash/toLower';
import { colorOpacity, customTheme } from 'theme';

import { formatNumberWithComma, getIconColor } from 'components/DataDisplay';
import { CustomTooltip } from 'components/DataDisplay/Tooltip/CustomTooltip';
import { formatTooltip } from 'components/DataDisplay/Tooltip/utils';
import formatNumber from 'components/DataDisplay/Utils/formatNumber';
import { toPascalCase, toTitleCase } from 'utils/string';

import { Tag } from '../Tag';

export const renderSeverity = ({ value }) => {
  return (
    <Tag
      label={toPascalCase(value)}
      styles={{ tag: { bg: toLower(value), color: 'white' } }}
    />
  );
};

//value: critical
export const renderSeverityBubble = ({ value }) => {
  return (
    <HStack spacing={2} w="full">
      <Box boxSize={2.5} bg={toLower(value)} borderRadius={2} />
      <Box>{toPascalCase(value)}</Box>
    </HStack>
  );
};

export const renderSeverityBar = ({
  value,
  isInline = true,
  reversed = false,
  showLabel = true,
  spacing = isInline ? 2 : 1,
  equalWidth = false,
  labelStyles = {},
}) => {
  const renderBox = index => {
    const severity = toLower(value);
    let bg = severity;
    const MIN_HEIGHT = 3;
    const STEP = 3;
    if (
      (severity === 'high' && index > 3) ||
      (severity === 'medium' && index > 2) ||
      (severity === 'low' && index > 1) ||
      (severity === 'unknown' && index > 0)
    )
      bg = 'gray.200';
    return (
      <Box
        w="5px"
        h={`${MIN_HEIGHT + index * STEP}px`}
        bg={bg}
        borderRadius={2.5}
      />
    );
  };

  return (
    <CustomTooltip
      label={formatTooltip({
        header: 'Severity',
        content: toTitleCase(value),
      })}
    >
      <Stack
        spacing={spacing}
        w="full"
        direction={isInline ? 'row' : 'column'}
        //align={isInline ? 'center' : 'default'}
        align="baseline"
      >
        {(!isInline || reversed) && showLabel && (
          <Box w={equalWidth ? 'full' : 'default'} {...labelStyles}>
            {toPascalCase(value)}
          </Box>
        )}
        <HStack
          spacing={0.5}
          w={equalWidth ? 'full' : 'default'}
          align="baseline"
        >
          {renderBox(1)}
          {renderBox(2)}
          {renderBox(3)}
          {renderBox(4)}
        </HStack>
        {isInline && !reversed && showLabel && (
          <Box w={equalWidth ? 'full' : 'default'} {...labelStyles}>
            {toPascalCase(value)}
          </Box>
        )}
      </Stack>
    </CustomTooltip>
  );
};

export const renderSeverityCountBubble = (severity, value) => {
  const color = getIconColor(severity);
  return (
    <CustomTooltip
      label={
        <Stack spacing={0}>
          <Box color="orange">SEVERITY</Box>
          <Box>{toTitleCase(severity)}</Box>
        </Stack>
      }
    >
      <Box
        px={1}
        minW={5}
        color={color}
        border="0.5px solid"
        bg={colorOpacity(color, 0.2)}
        borderRadius={6}
        //fontSize="sm"
      >
        <Center>{formatNumberWithComma(value ?? 0)}</Center>
      </Box>
      {/*<HStack spacing={1} align="center" w="full">
        <Box boxSize={2.5} bg={color} borderRadius={2} />
        <Box>{formatNumber(value ?? 0)}</Box>
      </HStack>*/}
    </CustomTooltip>
  );
};

// value => {CRITICAL: 1, MEDIUM: 2}
export const renderSeverityCountTags = ({ value }) => {
  const renderTag = (severity, value) => (
    <CustomTooltip
      label={
        <Stack spacing={0}>
          <Box color="orange">SEVERITY</Box>
          <Box>{toTitleCase(severity)}</Box>
          {/*<Box>{formatNumberWithComma(value)}</Box>*/}
        </Stack>
      }
    >
      <Tag
        label={value ? formatNumber(value) : 0}
        styles={{
          tag: {
            bg: colorOpacity(
              customTheme.colors?.[severity?.toLowerCase()],
              0.2,
            ),
            color: severity?.toLowerCase(),
            mr: 0,
            py: 0.5,
            borderRadius: 6,
            px: 2,
            borderColor: severity?.toLowerCase(),
          },
          label: { color: severity?.toLowerCase(), fontSize: 'md' },
        }}
      />
    </CustomTooltip>
  );

  const critical = value?.CRITICAL || value?.critical || value?.Critical;
  const high = value?.HIGH || value?.high || value?.High;
  const medium = value?.MEDIUM || value?.medium || value?.Medium;
  const low = value?.LOW || value?.low || value?.Low;

  return (
    <HStack spacing={1} align={'center'}>
      {!!critical && renderTag('critical', critical)}
      {!!high && renderTag('high', high)}
      {!!medium && renderTag('medium', medium)}
      {!!low && renderTag('low', low)}
    </HStack>
  );
};

export const renderSeverityBarWithCount = ({ value, count = 0 }) => {
  return (
    <HStack spacing={1} w="full">
      <Box>{renderSeverityBar({ value })}</Box>
      <Box>({count})</Box>
    </HStack>
  );
};

export const renderSeverityInSelect = severity => (
  <HStack w="full">
    <Box w={16}>{toTitleCase(severity)}</Box>
    <Box>
      {renderSeverityBar({
        value: severity?.toLowerCase(),
        showLabel: false,
      })}
    </Box>
  </HStack>
);

export const severityOptions = [
  {
    label: renderSeverityInSelect('critical'),
    value: 'critical',
  },
  {
    label: renderSeverityInSelect('high'),
    value: 'high',
  },
  {
    label: renderSeverityInSelect('medium'),
    value: 'medium',
  },
  {
    label: renderSeverityInSelect('low'),
    value: 'low',
  },
];

export const capsSeverityOptions = [
  {
    label: renderSeverityInSelect('critical'),
    value: 'CRITICAL',
  },
  {
    label: renderSeverityInSelect('high'),
    value: 'HIGH',
  },
  {
    label: renderSeverityInSelect('medium'),
    value: 'MEDIUM',
  },
  {
    label: renderSeverityInSelect('low'),
    value: 'LOW',
  },
];

export const severityOptionsWithAll = [
  { label: 'Severity', value: undefined },
  ...severityOptions,
];
